import { useAnalytics } from '../../../../../analytics'
import type { InsightDrillTarget } from '../../../../../data/ServerApiClient'
import { setPendingCfoGlDetailHandoff } from '../../cfoGlDetailHandoff'
import { CFO_EVENTS, CFO_SURFACES } from '../../cfoTelemetry'
import {
  LENS_LABEL,
  decisionEdgeClass,
  decisionKindLabel,
  decisionLens,
  glHandoffForDrill,
  sortDecisions,
} from '../ceoCockpitModel'
import type { CeoCockpitResponse, CeoDecision, CeoDimensionKind } from '../ceoCockpitTypes'

export type CeoDecisionRailProps = {
  payload: CeoCockpitResponse
  onOpenCfoCore?: (target?: InsightDrillTarget | null) => void
  onNavSelect: (navId: string) => void
  onExplain: (decision: CeoDecision) => void
  /** Sets the operating lens on the dimension panel (the "See by …" affordance). */
  onLensChange?: (lens: CeoDimensionKind) => void
}

/** Section ids on the CEO page that a `tab: 'ceo'` drill can land on. */
const CEO_VIEW_SECTION: Record<string, string> = {
  dimension: 'ceo-dimensions',
  dimensions: 'ceo-dimensions',
  saas: 'ceo-saas',
  inventory: 'ceo-inventory',
  labor: 'ceo-labor',
  customers: 'ceo-customers',
  cash: 'ceo-cash',
  pace: 'ceo-pace',
  scorecard: 'ceo-scorecard',
  goals: 'ceo-goals',
}

function scrollToCeoSection(id: string): boolean {
  if (typeof document === 'undefined') return false
  const el = document.getElementById(id)
  if (!el) return false
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } catch {
    el.scrollIntoView()
  }
  return true
}

export function CeoDecisionRail({ payload, onOpenCfoCore, onNavSelect, onExplain, onLensChange }: CeoDecisionRailProps) {
  const analytics = useAnalytics()
  const cards = sortDecisions(payload.decisions).slice(0, 5)
  if (!cards.length) {
    return (
      <section className="ceo-section rise" id="ceo-this-morning">
        <p className="ceo-kicker">This morning</p>
        <div
          className="ceo-empty"
          data-testid="ceo-decision-card"
          data-fg-tour="ceo-decision-card"
        >
          Nothing needs a decision this morning. The books are quiet.
        </div>
      </section>
    )
  }

  const trackOpen = (decision: CeoDecision, target: string, extra?: Record<string, unknown>) => {
    analytics.track({
      event: CFO_EVENTS.ceoPanelOpen,
      surface: CFO_SURFACES.ceo,
      props: {
        panel: 'decision',
        id: decision.id,
        rule: decision.rule,
        kind: decision.kind,
        tab: decision.drill?.tab || null,
        view: decision.drill?.view || null,
        target,
        ...(extra || {}),
      },
    })
  }

  const open = (decision: CeoDecision) => {
    const target = openDecision(decision, onOpenCfoCore, onNavSelect)
    trackOpen(decision, target)
  }

  const seeBy = (decision: CeoDecision, lens: CeoDimensionKind) => {
    onLensChange?.(lens)
    // Let the lens state commit before scrolling so the panel has re-rendered.
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => scrollToCeoSection('ceo-dimensions'))
    } else {
      scrollToCeoSection('ceo-dimensions')
    }
    trackOpen(decision, 'lens', { lens })
  }

  return (
    <section className="ceo-section rise" id="ceo-this-morning">
      <p className="ceo-kicker">This morning</p>
      <div className="ceo-decision-grid">
        {cards.map((decision, index) => {
          const lens = decisionLens(decision)
          const evidence = Array.isArray(decision.evidence) ? decision.evidence : []
          return (
            <article
              key={decision.id}
              className={`card edge ${decisionEdgeClass(decision)}${index === 0 ? ' lead' : ''}`}
              data-testid="ceo-decision-card"
              data-fg-tour="ceo-decision-card"
            >
              <div className="body ceo-decision-body">
                <span className="ceo-kind">{decisionKindLabel(decision.kind)}</span>
                <h3>{decision.headline}</h3>
                <p>{decision.body}</p>
                {evidence.length ? (
                  <div className="ceo-evidence">
                    {evidence.map((item, i) => (
                      <span key={`${decision.id}-${item.label}-${i}`} className={item.tone ? `is-${item.tone}` : undefined}>
                        {item.label ? <em>{item.label} · </em> : null}
                        {item.display}
                        {item.delta_display ? <small> · {item.delta_display}</small> : null}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="ceo-decision-footer">
                  <button type="button" onClick={() => open(decision)}>
                    Open →
                  </button>
                  {lens && onLensChange ? (
                    <button type="button" onClick={() => seeBy(decision, lens)} data-testid="ceo-see-by-lens" data-lens={lens}>
                      See by {LENS_LABEL[lens]}
                    </button>
                  ) : null}
                  <button type="button" onClick={() => onExplain(decision)}>Explain</button>
                </div>
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}

/** Routes a decision's drill; returns a short label of where it went (for telemetry). */
function openDecision(
  decision: CeoDecision,
  onOpenCfoCore: CeoDecisionRailProps['onOpenCfoCore'],
  onNavSelect: (navId: string) => void,
): string {
  const handoff = glHandoffForDrill(decision.drill, decision.headline)
  if (handoff) setPendingCfoGlDetailHandoff(handoff)
  if (decision.drill?.tab === 'gl_detail' || handoff) {
    onNavSelect('gl_detail')
    return 'gl_detail'
  }
  if (decision.drill?.tab === 'ceo') {
    onNavSelect('ceo')
    const section = CEO_VIEW_SECTION[String(decision.drill.view || '')] || null
    if (section) scrollToCeoSection(section)
    return section ? `ceo:${section}` : 'ceo'
  }
  onOpenCfoCore?.(decision.drill)
  return decision.drill?.tab ? `cfo:${decision.drill.tab}` : 'cfo'
}
